/**
 * Typed REST API surface for the Hotline backend.
 *
 * One method per backend route, grouped by domain. All admin reads/writes accept
 * an optional `orgId` — meta-admin must pass it (no implicit cross-tenant data);
 * org-admin omits it (the server pins them to their own org and 403s a mismatch).
 *
 * Route paths mirror the NestJS controllers:
 *  - auth:            POST /auth/{login,register,refresh,invite}, GET /auth/me
 *  - orgs:            /admin/orgs (+ :id/suspend, :id/reactivate)
 *  - users:           /admin/users
 *  - widget instances: /admin/widget-instances  (config editor CRUD)
 *  - tickets:         /admin/tickets (+ :id, PATCH :id)
 *  - comments:        /admin/tickets/:sessionId/comments
 *  - clusters:        /admin/clusters (+ recompute)
 *  - email:           /admin/tickets/:id/{reply,emails}
 */
import type {
  AuthResponse,
  Comment,
  ListTicketsQuery,
  LoginRequest,
  MagicLinkRequest,
  MagicLinkRequestResult,
  MeResponse,
  Organization,
  RegisterRequest,
  Ticket,
  TicketListResponse,
  TokenPair,
  UpdateOrganization,
  UpdateTicket,
  UpdateWidgetInstance,
  User,
  WidgetInstance,
  WidgetInstanceSecret,
  CreatedMcpToken,
  McpToken,
} from '@hotline/contracts';

import type { HttpClient } from './http.js';
import type {
  Cluster,
  ClusterListResponse,
  CreateCommentInput,
  CreateOrganizationInput,
  CreateWidgetInstanceInput,
  EmailLog,
  InviteUserInput,
  RecomputeClusters,
  RecomputeResult,
  ReplyEmail,
} from './types.js';

/** Optional org scope passed by meta-admin on org-scoped routes. */
type Scope = { orgId?: string };

/** Build a query object carrying the org scope (drops undefined). */
function scopeQuery(scope?: Scope): Record<string, string | undefined> {
  return { orgId: scope?.orgId };
}

export class HotlineApi {
  constructor(private readonly http: HttpClient) {}

  // ── Auth ──────────────────────────────────────────────────────────────────

  /** Log in with email + password; persists the returned token pair. */
  async login(body: LoginRequest): Promise<AuthResponse> {
    const res = await this.http.request<AuthResponse>('/auth/login', {
      method: 'POST',
      body,
      auth: false,
    });
    this.http.setTokens(res.tokens);
    return res;
  }

  /** Self-register (first-run admin / new org); persists the token pair. */
  async register(body: RegisterRequest): Promise<AuthResponse> {
    const res = await this.http.request<AuthResponse>('/auth/register', {
      method: 'POST',
      body,
      auth: false,
    });
    this.http.setTokens(res.tokens);
    return res;
  }

  /**
   * Sign in with Google: exchange the GIS ID token (`credential`) for a Hotline
   * session; persists the returned token pair.
   */
  async loginWithGoogle(credential: string): Promise<AuthResponse> {
    const res = await this.http.request<AuthResponse>('/auth/google', {
      method: 'POST',
      body: { credential },
      auth: false,
    });
    this.http.setTokens(res.tokens);
    return res;
  }

  /** Request a passwordless magic-link email (always acknowledges; no tokens). */
  requestMagicLink(body: MagicLinkRequest): Promise<MagicLinkRequestResult> {
    return this.http.request<MagicLinkRequestResult>('/auth/magic-link', {
      method: 'POST',
      body,
      auth: false,
    });
  }

  /** Consume a magic-link token (from the emailed URL); persists the token pair. */
  async verifyMagicLink(token: string): Promise<AuthResponse> {
    const res = await this.http.request<AuthResponse>('/auth/magic-link/verify', {
      method: 'POST',
      body: { token },
      auth: false,
    });
    this.http.setTokens(res.tokens);
    return res;
  }

  /** Exchange the stored refresh token for a fresh pair. */
  refresh(): Promise<TokenPair | null> {
    return this.http.refreshTokens();
  }

  /** Resolve the current principal from the access token. */
  /**
   * Who am I, and what may I do — answered from the server's database, so a
   * restored session reflects the CURRENT role rather than the one the token
   * was minted with.
   */
  me(): Promise<MeResponse> {
    return this.http.request<MeResponse>('/auth/me');
  }

  /** Invite a user (org-admin → own org; meta-admin → any org/role). */
  invite(body: InviteUserInput): Promise<unknown> {
    return this.http.request('/auth/invite', { method: 'POST', body });
  }

  /** Clear the persisted session (client-side logout). */
  logout(): void {
    this.http.setTokens(null);
  }

  // ── Organizations (meta-admin) ──────────────────────────────────────────────

  listOrganizations(): Promise<Organization[]> {
    return this.http.request<Organization[]>('/admin/orgs');
  }

  getOrganization(id: string): Promise<Organization> {
    return this.http.request<Organization>(`/admin/orgs/${id}`);
  }

  createOrganization(body: CreateOrganizationInput): Promise<Organization> {
    return this.http.request<Organization>('/admin/orgs', {
      method: 'POST',
      body,
    });
  }

  updateOrganization(
    id: string,
    body: UpdateOrganization,
  ): Promise<Organization> {
    return this.http.request<Organization>(`/admin/orgs/${id}`, {
      method: 'PATCH',
      body,
    });
  }

  suspendOrganization(id: string): Promise<Organization> {
    return this.http.request<Organization>(`/admin/orgs/${id}/suspend`, {
      method: 'POST',
    });
  }

  reactivateOrganization(id: string): Promise<Organization> {
    return this.http.request<Organization>(`/admin/orgs/${id}/reactivate`, {
      method: 'POST',
    });
  }

  // ── Users ───────────────────────────────────────────────────────────────────

  listUsers(scope?: Scope): Promise<User[]> {
    return this.http.request<User[]>('/admin/users', {
      query: scopeQuery(scope),
    });
  }

  inviteUser(body: InviteUserInput): Promise<unknown> {
    return this.http.request('/admin/users', { method: 'POST', body });
  }

  // ── Widget instances (config editor) ────────────────────────────────────────

  listWidgetInstances(scope?: Scope): Promise<WidgetInstance[]> {
    return this.http.request<WidgetInstance[]>('/admin/widget-instances', {
      query: scopeQuery(scope),
    });
  }

  getWidgetInstance(id: string, scope?: Scope): Promise<WidgetInstance> {
    return this.http.request<WidgetInstance>(`/admin/widget-instances/${id}`, {
      query: scopeQuery(scope),
    });
  }

  createWidgetInstance(
    body: CreateWidgetInstanceInput,
    scope?: Scope,
  ): Promise<WidgetInstance & Partial<WidgetInstanceSecret>> {
    return this.http.request('/admin/widget-instances', {
      method: 'POST',
      body,
      query: scopeQuery(scope),
    });
  }

  updateWidgetInstance(
    id: string,
    body: UpdateWidgetInstance,
    scope?: Scope,
  ): Promise<WidgetInstance> {
    return this.http.request<WidgetInstance>(
      `/admin/widget-instances/${id}`,
      { method: 'PATCH', body, query: scopeQuery(scope) },
    );
  }

  /** Rotate (or reveal once) the instance secret key. */
  rotateWidgetInstanceSecret(
    id: string,
    scope?: Scope,
  ): Promise<WidgetInstanceSecret> {
    return this.http.request<WidgetInstanceSecret>(
      `/admin/widget-instances/${id}/secret`,
      { method: 'POST', query: scopeQuery(scope) },
    );
  }

  // ── MCP integration tokens ──────────────────────────────────────────────────

  /** Mint an MCP integration token; the raw value is in THIS response only. */
  createMcpToken(instanceId: string, label: string, scope?: Scope): Promise<CreatedMcpToken> {
    return this.http.request<CreatedMcpToken>(
      `/admin/widget-instances/${instanceId}/mcp-tokens`,
      { method: 'POST', body: { label }, query: scopeQuery(scope) },
    );
  }

  listMcpTokens(instanceId: string, scope?: Scope): Promise<McpToken[]> {
    return this.http.request<McpToken[]>(
      `/admin/widget-instances/${instanceId}/mcp-tokens`,
      { method: 'GET', query: scopeQuery(scope) },
    );
  }

  revokeMcpToken(instanceId: string, tokenId: string, scope?: Scope): Promise<void> {
    return this.http.request<void>(
      `/admin/widget-instances/${instanceId}/mcp-tokens/${tokenId}`,
      { method: 'DELETE', query: scopeQuery(scope) },
    );
  }

  // ── MCP tokens, organization-scoped (meta-admin) ────────────────────────────

  /**
   * Organization tokens span every project in the org, so they live on the
   * `/admin/orgs` surface (META_ADMIN) rather than the per-instance one. The
   * `_org` suffix keeps the two families impossible to confuse at a call site.
   */
  createMcpTokenForOrg(organizationId: string, label: string): Promise<CreatedMcpToken> {
    return this.http.request<CreatedMcpToken>(`/admin/orgs/${organizationId}/mcp-tokens`, {
      method: 'POST',
      body: { label },
    });
  }

  listMcpTokensForOrg(organizationId: string): Promise<McpToken[]> {
    return this.http.request<McpToken[]>(`/admin/orgs/${organizationId}/mcp-tokens`, {
      method: 'GET',
    });
  }

  revokeMcpTokenForOrg(organizationId: string, tokenId: string): Promise<void> {
    return this.http.request<void>(`/admin/orgs/${organizationId}/mcp-tokens/${tokenId}`, {
      method: 'DELETE',
    });
  }

  // ── Tickets ──────────────────────────────────────────────────────────────────

  listTickets(
    query?: Partial<ListTicketsQuery>,
    scope?: Scope,
  ): Promise<TicketListResponse> {
    return this.http.request<TicketListResponse>('/admin/tickets', {
      query: { ...query, ...scopeQuery(scope) } as Record<
        string,
        string | number | boolean | undefined
      >,
    });
  }

  getTicket(id: string, scope?: Scope): Promise<Ticket> {
    return this.http.request<Ticket>(`/admin/tickets/${id}`, {
      query: scopeQuery(scope),
    });
  }

  updateTicket(
    id: string,
    body: UpdateTicket,
    scope?: Scope,
  ): Promise<Ticket> {
    return this.http.request<Ticket>(`/admin/tickets/${id}`, {
      method: 'PATCH',
      body,
      query: scopeQuery(scope),
    });
  }

  // ── Comments ─────────────────────────────────────────────────────────────────

  listComments(sessionId: string, scope?: Scope): Promise<Comment[]> {
    return this.http.request<Comment[]>(
      `/admin/tickets/${sessionId}/comments`,
      { query: scopeQuery(scope) },
    );
  }

  createComment(
    sessionId: string,
    body: CreateCommentInput,
    scope?: Scope,
  ): Promise<Comment> {
    return this.http.request<Comment>(
      `/admin/tickets/${sessionId}/comments`,
      { method: 'POST', body, query: scopeQuery(scope) },
    );
  }

  // ── Clusters / trends ─────────────────────────────────────────────────────────

  listClusters(scope?: Scope): Promise<ClusterListResponse> {
    return this.http.request<ClusterListResponse>('/admin/clusters', {
      query: scopeQuery(scope),
    });
  }

  getCluster(id: string, scope?: Scope): Promise<Cluster> {
    return this.http.request<Cluster>(`/admin/clusters/${id}`, {
      query: scopeQuery(scope),
    });
  }

  recomputeClusters(
    body: RecomputeClusters = {},
    scope?: Scope,
  ): Promise<RecomputeResult> {
    return this.http.request<RecomputeResult>('/admin/clusters/recompute', {
      method: 'POST',
      body,
      query: scopeQuery(scope),
    });
  }

  // ── Closed-loop email ──────────────────────────────────────────────────────────

  replyToTicket(
    id: string,
    body: ReplyEmail,
    scope?: Scope,
  ): Promise<EmailLog> {
    return this.http.request<EmailLog>(`/admin/tickets/${id}/reply`, {
      method: 'POST',
      body,
      query: scopeQuery(scope),
    });
  }

  listTicketEmails(id: string, scope?: Scope): Promise<EmailLog[]> {
    return this.http.request<EmailLog[]>(`/admin/tickets/${id}/emails`, {
      query: scopeQuery(scope),
    });
  }
}
